import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Subtitle } from 'pages/Home/styles'
import {
  TodayMeetings,
  TodayMeetingsHeader,
  MeetingsContainer,
  MeetingCard,
  MeetingInfo,
  ClientName,
  MeetingDateTime,
  MeetingActions,
  ActionButton,
  NoMeetingsMessage
} from './styles'
import dayjs from 'dayjs'
import { ParcelCollection } from 'globals/models/ParcelCollection'
import colors from 'globals/colors'
import Dialog from 'components/Dialog'
import {
  PARCEL_CANCEL_APPOINTMENT,
  PARCEL_COLLECTION_HONORED_MUTATION,
  PARCEL_COLLECTION_MISSED_MUTATION
} from 'pages/Home/mutations'
import { useMutation } from '@apollo/client'

const TodayMeetingsTile: React.FC<{ parcelCollections: ParcelCollection[]; refetch: () => void }> = ({
  parcelCollections,
  refetch
}) => {
  const { t } = useTranslation()

  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [showValidateDialog, setShowValidateDialog] = useState(false)
  const [showMissedDialog, setShowMissedDialog] = useState(false)
  const [parcelCollectionState, setParcelCollectionState] = useState<ParcelCollection | null>(null)

  const [cancelParcelAppointment, { loading: cancelParcelAppointmentLoading }] = useMutation(PARCEL_CANCEL_APPOINTMENT)
  const [parcelCollectionHonored, { loading: parcelCollectionHonoredLoading }] = useMutation(
    PARCEL_COLLECTION_HONORED_MUTATION
  )
  const [parcelCollectionMissed, { loading: parcelCollectionMissedLoading }] = useMutation(
    PARCEL_COLLECTION_MISSED_MUTATION
  )

  const handleConfirmCancel = (parcelCollectionId: string) => {
    cancelParcelAppointment({
      variables: {
        parcelCollectionId: parcelCollectionId
      }
    }).then(() => {
      refetch()
      return setShowCancelDialog(false)
    })
  }

  const handleConfirmValidate = (parcelCollectionId: string) => {
    parcelCollectionHonored({ variables: { parcelCollectionId: parcelCollectionId } }).then(() => {
      refetch()
      return setShowValidateDialog(false)
    })
  }

  const handleConfirmMissed = (parcelCollectionId: string) => {
    parcelCollectionMissed({ variables: { parcelCollectionId: parcelCollectionId } }).then(() => {
      refetch()
      return setShowMissedDialog(false)
    })
  }

  const handleCancel = (action: 'cancel' | 'validate' | 'missed', parcelCollection: ParcelCollection) => {
    setParcelCollectionState(parcelCollection)
    switch (action) {
      case 'cancel':
        setShowCancelDialog(true)
        break
      case 'validate':
        setShowValidateDialog(true)
        break
      case 'missed':
        setShowMissedDialog(true)
        break
      default:
        break
    }
  }

  return (
    <TodayMeetings>
      <TodayMeetingsHeader>
        <Subtitle smallMargin>{t('home.meetings.subtitle')}</Subtitle>
      </TodayMeetingsHeader>

      {parcelCollections?.length === 0 ? (
        <NoMeetingsMessage>{t('home.meetings.no-meetings')}</NoMeetingsMessage>
      ) : (
        <MeetingsContainer>
          {parcelCollections?.map((parcelCollection) => (
            <MeetingCard key={parcelCollection.id}>
              <MeetingInfo>
                <ClientName>
                  <span style={{ fontWeight: 'normal', color: colors.steelGrey }}>ID Colis:</span>{' '}
                  {parcelCollection.userMail?.id}
                </ClientName>
                <ClientName>
                  <span style={{ fontWeight: 'normal', color: colors.steelGrey }}>Dénom. Social:</span>{' '}
                  {parcelCollection.societyName}
                </ClientName>
                <ClientName>
                  <span style={{ fontWeight: 'normal', color: colors.steelGrey }}>Nom:</span>{' '}
                  {parcelCollection.customerFullname}
                </ClientName>
                <MeetingDateTime>
                  <span style={{ fontWeight: 'normal', color: colors.steelGrey }}>Date de rendez-vous:</span>{' '}
                  {dayjs(parcelCollection.startAt).format('DD/MM/YYYY à HH:mm')}
                </MeetingDateTime>
                <MeetingDateTime>
                  <span style={{ fontWeight: 'normal', color: colors.steelGrey }}>Date de destruction:</span>{' '}
                  {dayjs(parcelCollection.destructionExpectedDate).format('DD/MM/YYYY')}
                </MeetingDateTime>
                <ActionButton variant='cancel' onClick={() => handleCancel('cancel', parcelCollection)}>
                  {t('home.meetings.cancel')}
                </ActionButton>
              </MeetingInfo>

              <MeetingActions>
                <ActionButton variant='validate' onClick={() => handleCancel('validate', parcelCollection)}>
                  {t('home.meetings.validate')}
                </ActionButton>
                <ActionButton variant='missed' onClick={() => handleCancel('missed', parcelCollection)}>
                  {t('home.meetings.missed')}
                </ActionButton>
              </MeetingActions>
            </MeetingCard>
          ))}

          <Dialog visible={showCancelDialog} onClose={() => setShowCancelDialog(false)}>
            <div>
              <h3>{t('home.meetings.cancel-dialog.title')}</h3>
              <p>{t('home.meetings.cancel-dialog.description')}</p>
              <p>
                {t('home.meetings.cancel-dialog.message', {
                  societyName: parcelCollectionState?.societyName,
                  date: dayjs(parcelCollectionState?.startAt).format('DD/MM/YYYY à HH:mm')
                })}
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <ActionButton variant='cancel' onClick={() => setShowCancelDialog(false)}>
                  {t('home.meetings.cancel-dialog.cancel')}
                </ActionButton>
                <ActionButton
                  variant='validate'
                  disabled={cancelParcelAppointmentLoading}
                  onClick={() => handleConfirmCancel(parcelCollectionState?.id ?? '')}
                >
                  {t('home.meetings.cancel-dialog.confirm')}
                </ActionButton>
              </div>
            </div>
          </Dialog>

          <Dialog visible={showValidateDialog} onClose={() => setShowValidateDialog(false)}>
            <div>
              <h3>{t('home.meetings.validate-dialog.title')}</h3>
              <p>{t('home.meetings.validate-dialog.description')}</p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <ActionButton variant='cancel' onClick={() => setShowValidateDialog(false)}>
                  {t('home.meetings.validate-dialog.cancel')}
                </ActionButton>
                <ActionButton
                  variant='validate'
                  disabled={parcelCollectionHonoredLoading}
                  onClick={() => handleConfirmValidate(parcelCollectionState?.id ?? '')}
                >
                  {t('home.meetings.validate-dialog.confirm')}
                </ActionButton>
              </div>
            </div>
          </Dialog>

          <Dialog visible={showMissedDialog} onClose={() => setShowMissedDialog(false)}>
            <div>
              <h3>{t('home.meetings.missed-dialog.title')}</h3>
              <p>{t('home.meetings.missed-dialog.description')}</p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <ActionButton variant='cancel' onClick={() => setShowMissedDialog(false)}>
                  {t('home.meetings.missed-dialog.cancel')}
                </ActionButton>
                <ActionButton
                  variant='validate'
                  disabled={parcelCollectionMissedLoading}
                  onClick={() => handleConfirmMissed(parcelCollectionState?.id ?? '')}
                >
                  {t('home.meetings.missed-dialog.confirm')}
                </ActionButton>
              </div>
            </div>
          </Dialog>
        </MeetingsContainer>
      )}
    </TodayMeetings>
  )
}

export default TodayMeetingsTile
